import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiService } from '../../utils/api';
import { formatDate } from '../../utils/datetime';
import { ChevronLeft, ChevronRight, Image as ImageIcon, MapPin, Play, X } from 'lucide-react';

const YOUTUBE_EMBED = /youtube(?:-nocookie)?\.com\/embed\/([\w-]{6,})/;

export const videoThumbnail = (item) => {
  const match = YOUTUBE_EMBED.exec(item.embedUrl || '');
  return match ? `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg` : null;
};

const autoplayUrl = (url) => `${url}${url.includes('?') ? '&' : '?'}autoplay=1`;

/** Plays in place: a YouTube thumbnail until clicked, Vimeo (no public thumbnail) as its player straight away. */
export const VideoTile = ({ item, className = '' }) => {
  const [playing, setPlaying] = useState(false);
  const thumb = videoThumbnail(item);
  if (playing || !thumb) {
    return (
      <div className={`relative overflow-hidden rounded-lg bg-black ${className}`}>
        <iframe title={item.caption || 'Event video'} src={playing ? autoplayUrl(item.embedUrl) : item.embedUrl} loading="lazy"
          className="absolute inset-0 h-full w-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
      </div>
    );
  }
  return (
    <button type="button" onClick={() => setPlaying(true)} aria-label={`Play ${item.caption || 'video'}`}
      className={`group relative overflow-hidden rounded-lg bg-black text-left ${className}`}>
      <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform group-hover:scale-105" />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="rounded-full bg-black/60 p-4 group-hover:bg-red-600 transition-colors"><Play className="h-8 w-8 text-white fill-white" /></span>
      </span>
      {item.caption && (
        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white">{item.caption}</span>
      )}
    </button>
  );
};

/** Full-screen photo viewer with previous/next arrows, keyboard arrows and Esc. */
export const PhotoViewer = ({ photos, index, onIndex, onClose }) => {
  const photo = photos[index];
  const step = useCallback((delta) => onIndex((index + delta + photos.length) % photos.length), [index, photos.length, onIndex]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, onClose]);
  if (!photo) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button type="button" aria-label="Close" className="absolute top-4 right-4 text-white" onClick={onClose}><X className="h-7 w-7" /></button>
      {photos.length > 1 && (
        <>
          <button type="button" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white hover:bg-white/30"><ChevronLeft className="h-7 w-7" /></button>
          <button type="button" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white hover:bg-white/30"><ChevronRight className="h-7 w-7" /></button>
        </>
      )}
      <figure className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <img src={ApiService.galleryPhotoUrl(photo.id)} alt={photo.caption || ''} className="max-h-[80vh] mx-auto rounded" />
        <figcaption className="mt-2 text-center text-white text-sm">
          {photo.caption && <span>{photo.caption}</span>}
          {photos.length > 1 && <span className="ml-2 text-white/60">{index + 1} / {photos.length}</span>}
        </figcaption>
      </figure>
    </div>
  );
};

/** Photo and video tiles; photos open the viewer, videos play in place. */
export const GalleryGrid = ({ items }) => {
  const [open, setOpen] = useState(null);
  const photos = items.filter((item) => item.type === 'PHOTO');
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((item) => (item.type === 'PHOTO' ? (
          <button key={item.id} type="button" onClick={() => setOpen(photos.indexOf(item))} aria-label={item.caption || 'Photo'}
            className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-200 text-left">
            <img src={ApiService.galleryPhotoUrl(item.id)} alt={item.caption || ''} loading="lazy"
              className="h-full w-full object-cover transition-transform group-hover:scale-105" />
            {item.caption && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white">{item.caption}</span>
            )}
          </button>
        ) : (
          <VideoTile key={item.id} item={item} className="aspect-[4/3]" />
        )))}
      </div>
      {open !== null && <PhotoViewer photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </>
  );
};

export const AlbumCard = ({ album }) => (
  <Link to={`/gallery/${album.id}`} className="group block overflow-hidden rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow">
    <div className="aspect-[4/3] bg-gray-200 overflow-hidden">
      {album.coverId ? (
        <img src={ApiService.galleryPhotoUrl(album.coverId)} alt="" loading="lazy"
          className="h-full w-full object-cover transition-transform group-hover:scale-105" />
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <Play className="h-12 w-12 text-white/90" />
        </div>
      )}
    </div>
    <div className="p-3 space-y-1">
      <h3 className="font-semibold text-gray-900 line-clamp-2">{album.name}</h3>
      <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-1">
        {album.date && <span>{formatDate(album.date)}</span>}
        {album.location && <span className="inline-flex items-center"><MapPin className="h-3 w-3 mr-1" />{album.location}</span>}
        <span className="inline-flex items-center"><ImageIcon className="h-3 w-3 mr-1" />
          {[album.photos && `${album.photos} photo${album.photos === 1 ? '' : 's'}`, album.videos && `${album.videos} video${album.videos === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
        </span>
      </p>
    </div>
  </Link>
);
